import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { postDealershipUserUpdate } from 'PrivateAPI';

import { usePhotosContext } from 'Context/photos';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { SP_PROFILE_HUB } from 'Utilities/analytics/analyticsProfilingHub';
import { useFeatureFlags } from 'Utilities/hooks';
import { Seller } from 'Utilities/profiling/@types';

import { useVehicleProfilingContext } from '../DataFlow/VehicleProfilingContext.helpers';
import { usePhotosAppUrl } from '../Photos/Photos.hooks';
import {
  IS_ELECTRIC,
  IS_HYBRID,
  isLeasedBatteryModel,
  isServiceResupply,
} from '../VehicleProfiling.helpers';
import LocalTexts from '../VehicleProfilingTexts.json';

import * as Helper from './hubHelper';
import { COMPLETE_KEY, DETAILS_KEY, HISTORY_KEY, PART_OF_GLOBAL_PROGRESS, PERCENTAGES_COMPLETED } from './VehicleProfilingHub.constants';

export const useUpdateEmptyAddress = (isDetailsNotTouched: boolean) => {
  const { premiumOfferData, update } = useVehicleProfilingContext();
  const { sellerId } = premiumOfferData ?? {};

  useEffect(() => {
    if (isDetailsNotTouched) {
      const emptyData = {
        address1: '',
        address2: '',
        address3: '',
        city: '',
        postcode: '',
      };
      postDealershipUserUpdate(sellerId, emptyData).catch((error) =>
        window?.Sentry?.captureException(new Error(error)));
      update({ sellerData: emptyData as Seller });
    }
  }, [isDetailsNotTouched, sellerId, update]);
};

export const useVehicleProfilingSections = (shouldFetchShortURL = false) => {
  const [photosBlocked, setPhotosBlocked] = useState(true);

  const { vehicleLookupState: vehicleLookup } = useContext(VehicleLookupContext);
  const {
    premiumOfferData,
    resupply,
    sellerData,
    serviceHistoryResupplied,
  } = useVehicleProfilingContext();
  const {
    id,
    profilingStatus,
  } = premiumOfferData ?? {};

  const [, photosAppUrl] = usePhotosAppUrl({ id, shouldFetch: shouldFetchShortURL });

  const { resupplyPhotos, serviceHistoryPhotos, vehiclePhotos } = usePhotosContext();
  const photos = vehiclePhotos || [];
  const photosToRetakeCount = resupplyPhotos?.length;

  const isElectric = IS_ELECTRIC(vehicleLookup);
  const isHybrid = IS_HYBRID(vehicleLookup);
  const showBatteryOwnership = isLeasedBatteryModel(vehicleLookup);
  const featureFlags = useFeatureFlags();

  const sections = Helper.useGetSections({
    mapFunction: (key: string) => {
      let argsObj: any = {
        featureFlags,
        isElectric,
        isHybrid,
        PART_OF_GLOBAL_PROGRESS,
        photos,
        photoUrl: photosAppUrl,
        sellerData,
        serviceHistoryPhotos,
        showBatteryOwnership,
        state: premiumOfferData,
        text: LocalTexts,
      };

      if (key === HISTORY_KEY && isServiceResupply(profilingStatus || null)) {
        argsObj = { ...argsObj, hasResupplied: serviceHistoryResupplied };
      }

      const { current, isConfirmed, partOfGlobalProgress, progress, start } = Helper[`${key}PathAndProgress` as keyof typeof Helper](
        argsObj,
      ) || {};

      return {
        current,
        isConfirmed,
        key,
        partOfGlobalProgress,
        progress,
        start,
      };
    },
    photosBlocked,
    profilingStatus: profilingStatus ?? null,
    resupply,
    serviceHistoryResupplied,
  });

  useEffect(() => {
    setPhotosBlocked(sections[DETAILS_KEY].progress < 50);
  }, [sections]);

  return { photosBlocked, photosToRetakeCount, sections };
};

export const useVehicleProfilingActiveSection = ({
  isLoading,
  sections,
}: {
  isLoading: boolean;
  sections: Record<string, Helper.SectionsReturn>;
}) => {
  const loadEventSentRef = useRef(false);
  const sectionsProgress = useMemo(
    () => Object.fromEntries(Object.entries(sections).map(([key, section]) => [key, section.progress])),
    [sections],
  );

  const activeSection = useMemo(
    () => Object.values(sections).find(
      (section) => section.progress !== PERCENTAGES_COMPLETED && !section.isBlocked,
    ) ?? {} as Helper.SectionsReturn,
    [sections],
  );

  const activeSectionKey = activeSection?.key ?? '';
  const activeSectionInProgress = (activeSection?.progress ?? 0) > 0;

  const allSectionFilled = Object.values(sections).every((val) => val.progress === PERCENTAGES_COMPLETED);
  const activeSectionAnalyticsKey = allSectionFilled ? COMPLETE_KEY : activeSectionKey;

  useEffect(() => {
    if (!isLoading && !loadEventSentRef.current && activeSectionAnalyticsKey) {
      loadEventSentRef.current = true;
      SP_PROFILE_HUB.LOAD(JSON.stringify({
        active_step: activeSectionAnalyticsKey,
        active_step_in_progress: activeSectionInProgress,
        ...sectionsProgress,
      }));
    }
  }, [isLoading, sectionsProgress, activeSectionAnalyticsKey, activeSectionInProgress]);

  return {
    activeSection,
    activeSectionAnalyticsKey,
    activeSectionKey,
    allSectionFilled,
  };
};
